import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth/auth-context'
import { useMfaStatus } from './use-mfa-status'

type MfaEnrollmentStatus = 'not_enrolled' | 'enrolled' | 'grace_period'

interface EnrollResult {
  qrUri: string
  secret: string
  factorId: string
}

interface UseMfaReturn {
  // Enrollment
  enroll: () => Promise<EnrollResult>
  verify: (factorId: string, code: string) => Promise<boolean>
  unenroll: (factorId: string, code: string) => Promise<boolean>

  // Challenge (login)
  challenge: (factorId: string, code: string) => Promise<boolean>
  verifyBackupCode: (code: string) => Promise<boolean>

  // Backup codes
  getBackupCodes: () => Promise<string[]>
  regenerateBackupCodes: (factorId: string, code: string) => Promise<string[]>

  // Status
  mfaStatus: MfaEnrollmentStatus
  isMfaRequired: boolean
  loading: boolean
  error: string | null
  clearError: () => void
}

function generateBackupCodes(count: number = 10): string[] {
  const codes: string[] = []
  for (let i = 0; i < count; i++) {
    const code = Array.from(crypto.getRandomValues(new Uint8Array(4)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
    // Format as XXXX-XXXX
    codes.push(`${code.slice(0, 4)}-${code.slice(4, 8)}`)
  }
  return codes
}

export function useMfa(): UseMfaReturn {
  const { user } = useAuth()
  const { mfaEnabled, isGracePeriodExpired } = useMfaStatus()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => setError(null), [])

  // Determine MFA enrollment status
  const mfaStatus: MfaEnrollmentStatus = mfaEnabled
    ? 'enrolled'
    : isGracePeriodExpired
      ? 'not_enrolled'
      : 'grace_period'

  const isMfaRequired = isGracePeriodExpired && !mfaEnabled

  const enroll = useCallback(async (): Promise<EnrollResult> => {
    if (!user) throw new Error('Not authenticated')
    setLoading(true)
    setError(null)

    try {
      const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: 'ZeroShooting',
        friendlyName: 'ZeroShooting',
      })

      if (enrollErr || !data) {
        const msg = enrollErr?.message ?? 'Failed to start MFA enrollment'
        setError(msg)
        throw new Error(msg)
      }

      const email = user.email ?? 'user'
      const label = `ZERO - ${email}`
      const otpauthUri = `otpauth://totp/${encodeURIComponent(label)}?secret=${data.totp.secret}&issuer=ZeroShooting`

      return {
        qrUri: otpauthUri,
        secret: data.totp.secret,
        factorId: data.id,
      }
    } finally {
      setLoading(false)
    }
  }, [user])

  const verify = useCallback(async (factorId: string, code: string): Promise<boolean> => {
    setLoading(true)
    setError(null)

    try {
      const { data: challengeData, error: challengeErr } = await supabase.auth.mfa.challenge({
        factorId,
      })

      if (challengeErr || !challengeData) {
        const msg = challengeErr?.message ?? 'Failed to create MFA challenge'
        setError(msg)
        return false
      }

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      })

      if (verifyErr) {
        setError(verifyErr.message)
        return false
      }

      return true
    } finally {
      setLoading(false)
    }
  }, [])

  const challenge = useCallback(async (factorId: string, code: string): Promise<boolean> => {
    setLoading(true)
    setError(null)

    try {
      const { data: challengeData, error: challengeErr } = await supabase.auth.mfa.challenge({
        factorId,
      })

      if (challengeErr || !challengeData) {
        const msg = challengeErr?.message ?? 'Failed to create MFA challenge'
        setError(msg)
        return false
      }

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      })

      if (verifyErr) {
        setError(verifyErr.message)
        return false
      }

      return true
    } finally {
      setLoading(false)
    }
  }, [])

  const verifyBackupCode = useCallback(async (code: string): Promise<boolean> => {
    setLoading(true)
    setError(null)

    try {
      // Retrieve stored backup codes from user metadata
      const { data: userData } = await supabase.auth.getUser()
      const storedCodes = (userData?.user?.user_metadata?.backup_codes as string[]) ?? []

      const normalised = code.replace(/[-\s]/g, '').toUpperCase()
      const index = storedCodes.findIndex(
        (c: string) => c.replace(/[-\s]/g, '').toUpperCase() === normalised,
      )

      if (index === -1) {
        setError('Invalid backup code')
        return false
      }

      // Remove the used backup code
      const remainingCodes = [...storedCodes]
      remainingCodes.splice(index, 1)

      await supabase.auth.updateUser({
        data: { backup_codes: remainingCodes },
      })

      return true
    } finally {
      setLoading(false)
    }
  }, [])

  const unenroll = useCallback(
    async (factorId: string, code: string): Promise<boolean> => {
      setLoading(true)
      setError(null)

      try {
        // Verify the code first
        const verified = await challenge(factorId, code)
        if (!verified) return false

        const { error: unenrollErr } = await supabase.auth.mfa.unenroll({
          factorId,
        })

        if (unenrollErr) {
          setError(unenrollErr.message)
          return false
        }

        // Clear backup codes
        await supabase.auth.updateUser({
          data: { backup_codes: [] },
        })

        return true
      } finally {
        setLoading(false)
      }
    },
    [challenge],
  )

  const getBackupCodes = useCallback(async (): Promise<string[]> => {
    setLoading(true)
    setError(null)

    try {
      const codes = generateBackupCodes(10)

      // Store backup codes in user metadata
      await supabase.auth.updateUser({
        data: { backup_codes: codes },
      })

      return codes
    } finally {
      setLoading(false)
    }
  }, [])

  const regenerateBackupCodes = useCallback(
    async (factorId: string, code: string): Promise<string[]> => {
      setLoading(true)
      setError(null)

      try {
        // Verify the TOTP code first
        const verified = await challenge(factorId, code)
        if (!verified) {
          throw new Error('Invalid verification code')
        }

        const codes = generateBackupCodes(10)

        await supabase.auth.updateUser({
          data: { backup_codes: codes },
        })

        return codes
      } finally {
        setLoading(false)
      }
    },
    [challenge],
  )

  return {
    enroll,
    verify,
    unenroll,
    challenge,
    verifyBackupCode,
    getBackupCodes,
    regenerateBackupCodes,
    mfaStatus,
    isMfaRequired,
    loading,
    error,
    clearError,
  }
}
